import { GetAccount200ResponsePositionsInner } from "../codegen-api";
import { normalizeMarginType } from "./margin";

export type PositionWithIdentity = Partial<GetAccount200ResponsePositionsInner> & {
  position_key?: string;
  positionKey?: string;
  id?: string;
  instrument_id?: string;
  instrument_name?: string;
  market?: string;
  side?: string;
  isLong?: boolean;
  margin_type?: string;
  marginMode?: string | number;
};

const normalizeSide = (
  side?: string | boolean
): "buy" | "sell" | undefined => {
  if (side === true) return "buy";
  if (side === false) return "sell";

  const value = String(side || "").toLowerCase();
  if (value === "buy" || value === "long") return "buy";
  if (value === "sell" || value === "short") return "sell";
  return undefined;
};

// Margin-type canonicalisation lives in `utils/margin` and is shared with the
// order paths, so the numeric on-chain encoding is read the same way
// everywhere. This file previously kept a private copy that mapped 0 -> CROSS
// and 1 -> ISOLATED, which is inverted: the router encodes 0 = ISOLATED,
// 1 = CROSS (see the `marginMode` field in `usePerpOrder.place.ts`). Any
// identity derived from a numeric `marginMode` therefore carried the wrong
// margin type.

export function normalizePositionIdentity(
  position?: PositionWithIdentity
): PositionWithIdentity | undefined {
  if (!position) return undefined;

  const side =
    position.side ??
    (position.isLong === true
      ? "buy"
      : position.isLong === false
      ? "sell"
      : undefined);
  const marginType =
    position.margin_type ??
    (position.marginMode !== undefined
      ? String(position.marginMode).toLowerCase()
      : undefined);

  return {
    ...position,
    position_key: position.position_key ?? position.positionKey ?? position.id,
    positionKey: position.positionKey ?? position.position_key ?? position.id,
    instrument_id: position.instrument_id ?? position.market,
    side: normalizeSide(side),
    margin_type: normalizeMarginType(marginType),
  };
}

/** Stand-ins so a missing component can never collapse two legs into one id. */
const UNKNOWN_SIDE = "unknown-side";
const UNKNOWN_MARGIN = "unknown-margin";

/**
 * A stable id for one position.
 *
 * Every slot is always emitted. This used to build the composite with
 * `.filter(Boolean).join(":")`, which silently dropped whichever component was
 * missing — so a CROSS and an ISOLATED position on the same market and side
 * both collapsed to `BTC-PERP:buy` whenever `margin_type` was absent (it is
 * optional on the payload). Two different positions sharing one id meant
 * `positionById` could hand a modal the wrong leg, and React saw duplicate
 * keys. Explicit markers keep the id total and collision-free.
 *
 * Returns `undefined` only when there is no instrument to name at all, which
 * means the object is not a position we can act on.
 */
export function getPositionIdentity(
  position?: PositionWithIdentity
): string | undefined {
  const normalized = normalizePositionIdentity(position);
  if (!normalized) return undefined;

  // A server-issued key is exact — prefer it whenever one is present.
  const explicitKey =
    normalized.position_key || normalized.positionKey || normalized.id;
  if (explicitKey) return explicitKey;

  const instrumentRef = normalized.instrument_id || normalized.instrument_name;
  if (!instrumentRef) return undefined;

  return [
    instrumentRef,
    normalized.side || UNKNOWN_SIDE,
    normalized.margin_type || UNKNOWN_MARGIN,
  ].join(":");
}

export function getPositionIdentityCandidates(
  position?: PositionWithIdentity
): string[] {
  const normalized = normalizePositionIdentity(position);
  const identity = getPositionIdentity(normalized);
  if (!normalized) return identity ? [identity] : [];

  return [
    identity,
    normalized.position_key,
    normalized.positionKey,
    normalized.id,
    `${normalized.instrument_id}-${normalized.side}`,
    `${normalized.instrument_id}-${String(normalized.side).toLowerCase()}`,
    `${normalized.instrument_id}-${String(normalized.side).toUpperCase()}`,
    `${normalized.instrument_id}-${normalized.side}-${normalized.margin_type}`,
  ].filter(Boolean) as string[];
}
